import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DefaultImageDirective } from '@shared/directives';
import { HumanizePipe, TruncateTextPipe } from '@shared/pipes';
import { SponsoredAdEntity } from '@core/entities';
import { TippyModule } from '@ngneat/helipopper';
import { UseDashboard } from '@core/usecases';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GetAllowAdvertisement, isMobileView } from '@core/utils';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';

@UntilDestroy()
@Component({
  selector: 'app-banner-display',
  imports: [CommonModule, DefaultImageDirective, HumanizePipe, TruncateTextPipe, TippyModule],
  templateUrl: './banner-display.component.html',
  styleUrls: ['./banner-display.component.scss'],
})
export class BannerDisplayComponent implements OnInit, AfterViewInit, OnDestroy {
  sponsoredAds: SponsoredAdEntity[] = [];
  @Input() cssClass = '';
  currentSlide = 1;
  @ViewChild('imageSliderRef') imageSliderRef: ElementRef<HTMLElement>;
  imageSlider: KeenSliderInstance = null;
  isSliderReady = false;
  protected isMobileView = isMobileView();
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _useDashboard = new UseDashboard();

  get isAdvertisementEnabled() {
    return +GetAllowAdvertisement() === 0;
  }

  ngOnInit() {
    if (!this.isAdvertisementEnabled) return;
    this._fetchSponsoredAds();
  }

  ngAfterViewInit() {
    if (this.sponsoredAds.length > 1) {
      requestAnimationFrame(() => {
        this._initImageSlider();
      });
    }
  }

  onBannerClick(banner: SponsoredAdEntity) {
    if (!banner) return;
    this._useDashboard.updateBannerStats(banner.id).subscribe();
    window.open(banner.url, '_blank');
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  ngOnDestroy() {
    if (this.imageSlider) {
      this.imageSlider.destroy();
    }
  }

  private _fetchSponsoredAds() {
    this._useDashboard
      .getSponsoredAds()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (ads) => {
          this.sponsoredAds = ads;
          this._cdr.detectChanges();
          if (this.sponsoredAds.length > 1) {
            setTimeout(() => {
              this._initImageSlider();
            }, 100);
          }
        },
      });
  }

  private _initImageSlider() {
    if (!this.imageSliderRef?.nativeElement) return;

    try {
      this.imageSlider = new KeenSlider(
        this.imageSliderRef.nativeElement,
        {
          loop: true,
          slides: {
            perView: 1,
            spacing: 0,
          },
        },
        [
          (slider) => {
            let timeout: any;
            let mouseOver = false;

            function clearNextTimeout() {
              clearTimeout(timeout);
            }

            function nextTimeout() {
              clearTimeout(timeout);
              if (mouseOver) return;
              timeout = setTimeout(() => {
                slider.next();
              }, 5000);
            }

            slider.on('created', () => {
              // Set slider as ready after creation
              this.isSliderReady = true;
              this._cdr.detectChanges();

              slider.container.addEventListener('mouseover', () => {
                mouseOver = true;
                clearNextTimeout();
              });
              slider.container.addEventListener('mouseout', () => {
                mouseOver = false;
                nextTimeout();
              });
              nextTimeout();
            });

            slider.on('dragStarted', clearNextTimeout);
            slider.on('animationEnded', nextTimeout);
            slider.on('updated', nextTimeout);
          },
        ],
      );
    } catch (error) {
      console.error('Error initializing slider:', error);
    }
  }
}
